/**
 * useChecklistState Hook
 *
 * Manages checklist state with localStorage persistence for HALMS auditing.
 * Handles individual item completion, notes, and overall progress tracking.
 *
 * Features:
 * - Persistent state across sessions
 * - Individual item completion tracking
 * - Notes/comments per item
 * - Overall progress calculation
 * - Category-based grouping support
 * - Reset functionality
 *
 * Used for:
 * - Pre-audit checklists
 * - Official audit checklists (MHMS 2020)
 * - Application review checklists
 * - Document verification checklists
 */

import { useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

export type ChecklistItemStatus = 'pending' | 'completed' | 'not-applicable' | 'failed';

export interface ChecklistItem {
  id: string;
  label: string;
  description?: string;
  category?: string;
  required?: boolean;
  reference?: string; // MHMS clause reference
}

export interface ChecklistItemState {
  id: string;
  status: ChecklistItemStatus;
  notes?: string;
  completedAt?: string; // ISO date string
  completedBy?: string; // User ID
}

export interface ChecklistState {
  items: Record<string, ChecklistItemState>;
  metadata?: {
    checklistId: string;
    applicationId?: string;
    auditId?: string;
    startedAt?: string;
    lastUpdatedAt?: string;
  };
}

export interface ChecklistProgress {
  total: number;
  completed: number;
  pending: number;
  notApplicable: number;
  failed: number;
  percentage: number;
  requiredCompleted: number;
  requiredTotal: number;
  requiredPercentage: number;
}

interface UseChecklistStateReturn {
  state: ChecklistState;
  getItemState: (itemId: string) => ChecklistItemState | undefined;
  updateItemStatus: (itemId: string, status: ChecklistItemStatus) => void;
  updateItemNotes: (itemId: string, notes: string) => void;
  toggleItem: (itemId: string) => void;
  completeItem: (itemId: string, userId?: string) => void;
  resetItem: (itemId: string) => void;
  resetAll: () => void;
  getProgress: (items: ChecklistItem[]) => ChecklistProgress;
  getCategoryProgress: (items: ChecklistItem[], category: string) => ChecklistProgress;
  isAllRequiredCompleted: (items: ChecklistItem[]) => boolean;
}

/**
 * Hook for managing checklist state with localStorage persistence
 * @param storageKey - Unique key for localStorage (e.g., 'checklist-audit-123')
 * @param checklistId - Identifier for this checklist
 * @param applicationId - Optional application ID
 * @param auditId - Optional audit ID
 */
export function useChecklistState(
  storageKey: string,
  checklistId: string,
  applicationId?: string,
  auditId?: string
): UseChecklistStateReturn {
  const initialState: ChecklistState = {
    items: {},
    metadata: {
      checklistId,
      applicationId,
      auditId,
      startedAt: new Date().toISOString(),
      lastUpdatedAt: new Date().toISOString(),
    },
  };

  const [state, setState] = useLocalStorage<ChecklistState>(storageKey, initialState);

  // Get state for a specific item
  const getItemState = useCallback(
    (itemId: string): ChecklistItemState | undefined => {
      return state.items[itemId];
    },
    [state.items]
  );

  // Update item status
  const updateItemStatus = useCallback(
    (itemId: string, status: ChecklistItemStatus) => {
      setState((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [itemId]: {
            ...prev.items[itemId],
            id: itemId,
            status,
            completedAt: status === 'completed' ? new Date().toISOString() : prev.items[itemId]?.completedAt,
          },
        },
        metadata: {
          ...prev.metadata!,
          lastUpdatedAt: new Date().toISOString(),
        },
      }));
    },
    [setState]
  );

  // Update item notes
  const updateItemNotes = useCallback(
    (itemId: string, notes: string) => {
      setState((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [itemId]: {
            ...prev.items[itemId],
            id: itemId,
            status: prev.items[itemId]?.status || 'pending',
            notes,
          },
        },
        metadata: {
          ...prev.metadata!,
          lastUpdatedAt: new Date().toISOString(),
        },
      }));
    },
    [setState]
  );

  // Toggle item between completed and pending
  const toggleItem = useCallback(
    (itemId: string) => {
      const currentStatus = state.items[itemId]?.status || 'pending';
      const newStatus: ChecklistItemStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      updateItemStatus(itemId, newStatus);
    },
    [state.items, updateItemStatus]
  );

  // Complete an item with timestamp and user
  const completeItem = useCallback(
    (itemId: string, userId?: string) => {
      setState((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [itemId]: {
            ...prev.items[itemId],
            id: itemId,
            status: 'completed',
            completedAt: new Date().toISOString(),
            completedBy: userId,
          },
        },
        metadata: {
          ...prev.metadata!,
          lastUpdatedAt: new Date().toISOString(),
        },
      }));
    },
    [setState]
  );

  // Reset a specific item
  const resetItem = useCallback(
    (itemId: string) => {
      setState((prev) => {
        const newItems = { ...prev.items };
        delete newItems[itemId];
        return {
          ...prev,
          items: newItems,
          metadata: {
            ...prev.metadata!,
            lastUpdatedAt: new Date().toISOString(),
          },
        };
      });
    },
    [setState]
  );

  // Reset all items
  const resetAll = useCallback(() => {
    setState(initialState);
  }, [setState, initialState]);

  // Calculate progress for given items
  const getProgress = useCallback(
    (items: ChecklistItem[]): ChecklistProgress => {
      const total = items.length;
      let completed = 0;
      let pending = 0;
      let notApplicable = 0;
      let failed = 0;
      let requiredCompleted = 0;
      let requiredTotal = 0;

      items.forEach((item) => {
        const itemState = state.items[item.id];
        const status = itemState?.status || 'pending';

        if (status === 'completed') completed++;
        else if (status === 'pending') pending++;
        else if (status === 'not-applicable') notApplicable++;
        else if (status === 'failed') failed++;

        if (item.required) {
          requiredTotal++;
          if (status === 'completed') requiredCompleted++;
        }
      });

      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      const requiredPercentage = requiredTotal > 0 ? Math.round((requiredCompleted / requiredTotal) * 100) : 100;

      return {
        total,
        completed,
        pending,
        notApplicable,
        failed,
        percentage,
        requiredCompleted,
        requiredTotal,
        requiredPercentage,
      };
    },
    [state.items]
  );

  // Calculate progress for a specific category
  const getCategoryProgress = useCallback(
    (items: ChecklistItem[], category: string): ChecklistProgress => {
      const categoryItems = items.filter((item) => item.category === category);
      return getProgress(categoryItems);
    },
    [getProgress]
  );

  // Check if all required items are completed
  const isAllRequiredCompleted = useCallback(
    (items: ChecklistItem[]): boolean => {
      return items
        .filter((item) => item.required)
        .every((item) => state.items[item.id]?.status === 'completed');
    },
    [state.items]
  );

  return {
    state,
    getItemState,
    updateItemStatus,
    updateItemNotes,
    toggleItem,
    completeItem,
    resetItem,
    resetAll,
    getProgress,
    getCategoryProgress,
    isAllRequiredCompleted,
  };
}

export default useChecklistState;
